import { Intent } from '@blueprintjs/core';
import { GenericProperty } from 'vet-bones/components';

import * as copy from 'src/templates/copy';

export const countUniqueExistingProperties = (
  existingProperties: GenericProperty[],
  propertyToBeAdded?: string
) =>
  existingProperties
    .map(({ name }) => name)
    .concat(propertyToBeAdded ? [propertyToBeAdded] : [])
    .reduce(
      (agg, name) => ({
        ...agg,
        [name]: (agg[name] || 0) + 1,
      }),
      {} as Record<string, number>
    );

const getNameTooltip = (
  name: string,
  reservedProperties?: Set<string>,
  existingPropertyCounts?: Record<string, number>
) => {
  if (name === '') {
    return copy.components.genericPropertiesEditor.validation
      .missingValueTooltip;
  }
  if (reservedProperties?.has(name)) {
    return copy.components.genericPropertiesEditor.validation
      .reservedNameTooltip;
  }
  if ((existingPropertyCounts?.[name] || 0) > 1) {
    return copy.components.genericPropertiesEditor.validation
      .duplicatedNameTooltip;
  }
  return '';
};

export const isRowInvalid = (
  name: string,
  value: string,
  reservedProperties?: Set<string>,
  existingPropertyCounts?: Record<string, number>
) =>
  name === '' ||
  value === '' ||
  reservedProperties?.has(name) ||
  (existingPropertyCounts?.[name] || 0) > 1;

export const genericPropertyValidator =
  (
    hasAttemptedToCreateInvalidNewRow: boolean,
    reservedProperties: Set<string>,
    existingProperties: GenericProperty[]
  ) =>
  (isNewRow: boolean, name: string, value: string) => {
    const existingPropertyCounts = countUniqueExistingProperties(
      existingProperties,
      isNewRow ? name : undefined
    );
    const invalid = isRowInvalid(
      name,
      value,
      reservedProperties,
      existingPropertyCounts
    );
    const showThatRowIsInvalid =
      isNewRow && !hasAttemptedToCreateInvalidNewRow ? false : invalid;
    const nameTooltip =
      (showThatRowIsInvalid &&
        getNameTooltip(name, reservedProperties, existingPropertyCounts)) ||
      '';
    const valueTooltip =
      value === '' && showThatRowIsInvalid
        ? copy.components.genericPropertiesEditor.validation.missingValueTooltip
        : '';

    return {
      actionIntent: nameTooltip || valueTooltip ? Intent.DANGER : Intent.NONE,
      actionTooltip: nameTooltip || valueTooltip,
      disableAction: isNewRow && showThatRowIsInvalid,
      name: {
        inputIntent: nameTooltip ? Intent.DANGER : Intent.NONE,
        inputTooltip: nameTooltip,
      },
      value: {
        inputIntent: valueTooltip ? Intent.DANGER : Intent.NONE,
        inputTooltip: valueTooltip,
      },
    };
  };
